import { DEG2RAD } from '../math/index'
import type { Vec3 } from '../math/vec3'

/**
 * Pinhole camera used by both the tracker (MediaPipe face geometry: vertical FOV 63°)
 * and the renderer. Camera space: x right, y up, looking down −Z. Units cm.
 * Normalized image coords: x ∈ [0,1] left→right, y ∈ [0,1] top→bottom.
 */
export interface CameraModel {
  fovYDeg: number
  aspect: number // width / height
  near: number
  far: number
}

/** MediaPipe face-geometry defaults. */
export const DEFAULT_FOV_Y_DEG = 63
export const DEFAULT_NEAR_CM = 1
export const DEFAULT_FAR_CM = 10000

export function createCameraModel(aspect: number, fovYDeg = DEFAULT_FOV_Y_DEG): CameraModel {
  return { fovYDeg, aspect, near: DEFAULT_NEAR_CM, far: DEFAULT_FAR_CM }
}

/** Half-height of the view frustum at depth 1. */
export function tanHalfFovY(cam: CameraModel): number {
  return Math.tan((cam.fovYDeg * DEG2RAD) / 2)
}

/** Width of the visible frame (cm) at a given positive depth. */
export function frameWidthAtDepth(cam: CameraModel, depth: number): number {
  return 2 * depth * tanHalfFovY(cam) * cam.aspect
}

/** Point in camera space on the ray through normalized pixel (nx, ny) at positive depth `depth`. */
export function unprojectAtDepth(out: Vec3, cam: CameraModel, nx: number, ny: number, depth: number): Vec3 {
  const t = tanHalfFovY(cam)
  out[0] = (nx * 2 - 1) * t * cam.aspect * depth
  out[1] = (1 - ny * 2) * t * depth
  out[2] = -depth
  return out
}

/** Camera-space point → normalized image coords [nx, ny]. Returns null if behind the camera. */
export function projectToNormalized(out: [number, number], cam: CameraModel, p: Vec3): [number, number] | null {
  const depth = -p[2]
  if (depth <= 0) return null
  const t = tanHalfFovY(cam)
  out[0] = (p[0] / (t * cam.aspect * depth) + 1) / 2
  out[1] = (1 - p[1] / (t * depth)) / 2
  return out
}

/**
 * Move a camera-space point onto the ray through normalized pixel (nx, ny), keeping its depth.
 * Used to snap head-matrix-derived anchors to the actually observed 2-D landmark.
 */
export function snapToRay(out: Vec3, cam: CameraModel, p: Vec3, nx: number, ny: number): Vec3 {
  return unprojectAtDepth(out, cam, nx, ny, -p[2])
}
